import { useQueryClient } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import { useLocale } from 'next-intl'
import { useRef, KeyboardEvent } from 'react'

import { TKeywordMarker } from '@/entities/map'
import { roomQueries } from '@/entities/room'
import {
  isNilOrEmptyString,
  StringDateRange,
  useFormatDate,
} from '@/shared/lib'
import { type RangeDatePickerRef } from '@/shared/ui'

import { convertHistoryToLocation, convertMarkerToLocation } from './location'
import { useDateRange } from '../model/date-range'
import { useKeyword } from '../model/keyword'
import {
  convertToSearchHistory,
  type History,
} from '../model/search-history-store/history'
import { useSearchHistoryStore } from '../model/search-history-store/store'
import { useSelectedLocation } from '../model/selected-location'
import { type AutoCompleteInputRef } from '../ui/auto-complete-input'

export const MIN_KEYWORD_LENGTH = 2

type Props = {
  search: ({
    lat,
    lng,
    zoomLevel,
    startDate,
    endDate,
    keyword,
  }: {
    lat: number | null
    lng: number | null
    zoomLevel: number | null
    startDate?: string
    endDate?: string
    keyword: string
  }) => void
}

export const useSearch = ({ search }: Props) => {
  const locale = useLocale()
  const { formatDate, formatDateRange } = useFormatDate()

  const queryClient = useQueryClient()

  const periodPickerRef = useRef<RangeDatePickerRef>({
    focus: () => {},
  })

  const inputRef = useRef<AutoCompleteInputRef>({
    openAutoCompletePopup: () => {},
    closeAutoCompletePopup: () => {},
  })

  const historyActions = useSearchHistoryStore(state => state.actions)

  const { dateRange, updateDateRange } = useDateRange()

  const {
    keyword,
    hasKeyword,
    updateKeyword,
    focused: isAutoCompleteInputFocused,
    updateFocusStatus: updateAutoCompleteInputFocusStatus,
  } = useKeyword()

  const {
    selectedLocation,
    getLocationByKeyword,
    updateLocation,
    resetLocation,
  } = useSelectedLocation({ keyword })

  const hasLocationInHistory = (history: History) => {
    return !isNilOrEmptyString(history.name)
  }

  const getLocation = async () => {
    if (!hasKeyword()) {
      return { success: true, location: null }
    }

    if (keyword.length < MIN_KEYWORD_LENGTH) {
      return { success: false, location: null }
    }

    const location = await getLocationByKeyword()

    if (!location) {
      return { success: false, location: null }
    }

    return { success: true, location }
  }

  const getAutoCompleteInputValue = () => {
    if (!selectedLocation) return keyword

    if (isAutoCompleteInputFocused) {
      return selectedLocation.keyword
    }

    return selectedLocation.name
  }

  const moveFocusToPeriodPicker = () => {
    periodPickerRef.current.focus()
    updateAutoCompleteInputFocusStatus(false)
  }

  const handleKeywordChange = (location: TKeywordMarker | null) => {
    updateLocation(convertMarkerToLocation(location))

    if (location) {
      moveFocusToPeriodPicker()
    }
  }

  const handleDateRangeChange = (dateRange?: StringDateRange) => {
    updateDateRange(dateRange)
  }

  const handleRecentHistoryClick = (history: History) => {
    const { name, startDate, endDate, location, nextZoomLevel } = history

    const today = new Date().setHours(0, 0, 0, 0)
    const isBeforeToday = isBefore(startDate, today)

    if (isBeforeToday) {
      if (hasLocationInHistory(history)) {
        updateLocation(convertHistoryToLocation(history))
      }

      updateKeyword(name)
      updateDateRange(undefined)

      moveFocusToPeriodPicker()

      return
    }

    historyActions.addSearchHistory(history)

    search({
      lat: location?.lat || null,
      lng: location?.lng || null,
      zoomLevel: nextZoomLevel,
      startDate,
      endDate,
      keyword: name,
    })

    updateKeyword('')
    updateDateRange({
      from: startDate,
      to: endDate,
    })
  }

  const handleInputEnterKeyDown = async () => {
    const { success, location } = await getLocation()

    if (!success) {
      return
    }

    updateKeyword(location?.keyword || '')
    updateLocation(location)

    inputRef.current.closeAutoCompletePopup()

    moveFocusToPeriodPicker()
  }

  const handleInputFocus = () => {
    updateAutoCompleteInputFocusStatus(true)
  }

  const handleInputBlur = async () => {
    updateAutoCompleteInputFocusStatus(false)
    await queryClient.invalidateQueries({
      queryKey: roomQueries.recentViewedRooms().queryKey,
      refetchType: 'none',
    })
  }

  const handlePeriodPickerKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key === 'Enter') {
      void handleSubmit()
    }
  }

  const handleSubmit = async () => {
    const { success, location } = await getLocation()

    if (!success) {
      inputRef.current.openAutoCompletePopup()

      return
    }

    if (location || dateRange) {
      historyActions.addSearchHistory(
        convertToSearchHistory({ location, dateRange, locale }),
      )
    }

    search({
      lat: location?.lat || null,
      lng: location?.lng || null,
      zoomLevel: location?.nextZoomLevel || null,
      startDate: formatDate({ date: dateRange?.from, format: 'yyyy-MM-dd' }),
      endDate: formatDate({ date: dateRange?.to, format: 'yyyy-MM-dd' }),
      keyword: location?.name || '',
    })

    updateKeyword('')
    resetLocation()
  }

  return {
    autoCompleteInputProps: {
      value: getAutoCompleteInputValue(),
      onChange: updateKeyword,
      onFocus: handleInputFocus,
      onBlur: handleInputBlur,
      onKeywordSelect: handleKeywordChange,
      onRecentHistoryClick: handleRecentHistoryClick,
      onEnterKeyDown: handleInputEnterKeyDown,
      ref: inputRef,
    },
    periodPickerProps: {
      value: formatDateRange({ dateRange }),
      onChange: handleDateRangeChange,
      onEnterKeyDown: handlePeriodPickerKeyDown,
      ref: periodPickerRef,
    },
    handleSubmit,
  }
}
